import { useState } from 'react';
import { Card, Skeleton, Empty, Segmented } from 'antd';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { DollarOutlined } from '@ant-design/icons';
import './financial-dashboard.css';

const TYPE_ORDER = ['supplier', 'fixed', 'variable', 'tax'];

const PERSONA_OPTIONS = [
  { label: 'Total', value: 'total' },
  { label: 'Persona Moral', value: 'moral' },
  { label: 'Persona Física', value: 'fisica' },
];

const fmtAxis = (value) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
};

const fmtTooltip = (value, name) => [
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(value),
  name,
];

function getMXN(typeData, persona) {
  if (persona === 'total') return typeData.MXN?.total || 0;
  return typeData.by_person_type?.[persona]?.MXN?.total || 0;
}

function getUSD(typeData, persona) {
  if (persona === 'total') return typeData.USD?.total || 0;
  return typeData.by_person_type?.[persona]?.USD?.total || 0;
}

export default function ExpensesReport({ data = {}, loading, currentDateInfo }) {
  const [persona, setPersona] = useState('total');

  const byType = data.by_type || {};

  const chartData = TYPE_ORDER
    .filter((key) => byType[key])
    .map((key) => ({
      name: byType[key].label,
      MXN: getMXN(byType[key], persona),
      USD: getUSD(byType[key], persona),
    }));

  const isEmpty = chartData.length === 0 ||
    chartData.every((d) => d.MXN === 0 && d.USD === 0);

  return (
    <Card
      className="financial-card"
      title={
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <DollarOutlined /> Reporte de Gastos
        </span>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {currentDateInfo && (
            <span style={{ fontSize: '12px', color: '#888' }}>{currentDateInfo}</span>
          )}
          <Segmented
            size="small"
            options={PERSONA_OPTIONS}
            value={persona}
            onChange={setPersona}
          />
        </div>
      }
    >
      <Skeleton loading={loading} active paragraph={{ rows: 4 }}>
        {isEmpty ? (
          <Empty description="Sin gastos para el periodo seleccionado" />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 8, right: 32, left: 16, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={fmtAxis} style={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={80} style={{ fontSize: 12 }} />
              <Tooltip formatter={fmtTooltip} />
              <Legend />
              <Bar dataKey="MXN" fill="#fa8c16" radius={[0, 4, 4, 0]} barSize={14} />
              <Bar dataKey="USD" fill="#1890ff" radius={[0, 4, 4, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Skeleton>
    </Card>
  );
}
